import React, { Fragment, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import '../../../src/styles/date-time-picker.css';

import NotificationPopup from "../NotificationPopup";
import SvgIcon from "../Icons";
import EventDetailsStep from "./EventDetailsStep";
import ContactsStep from "./ContactsStep";

import './index.sass';


const BookingForm = ({ email, id, placeName, siteName, userCountryPhoneCode }) => {

  const [currentStep, setCurrentStep] = useState(1);
  const [values, setValues] = useState({});
  const [validationTrigger, setValidationTrigger] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);

  const formWrapperRef = useRef();


  useEffect(() => {
    window.addEventListener('orientationchange', () => setIsOpen(false));
    return () => {
      window.removeEventListener('orientationchange', () => setIsOpen(false));
    }
  }, []);


  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      window.addEventListener('click', handleClickOutside);
    } else {
      document.body.classList.remove('modal-open');
      window.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);


  const handleClickOutside = (e) => {
    const formWrapper = formWrapperRef && formWrapperRef.current;
    const target = e.target;
    const parent = target.parentNode;

    const isFormWrapper = formWrapper && (target === formWrapper || formWrapper.contains(target));
    const isDropdownOption = target.classList.contains('dropdown__option') || target.classList.contains('basic-dropdown__option');
    const isHidden = target.getAttribute('role') === 'hide-after-click' || parent.getAttribute('role') === 'hide-after-click';


    if (formWrapper && !isFormWrapper && !isDropdownOption && !isHidden) {
      setCurrentStep(1);
      setIsOpen(false);
      setValues({});
      setBookingSuccess(false);
    }
  };


  const submitForm = (data) => {
    const url = '/api/frontend/place_enquiries.json';
    const phone = data.phone_code + data.phone.replace(/[-\s()]/g, '');
    let formattedDate;

    delete data.phone_code;

    if (values.event_date) {
      let newDate = new Date(values.event_date);

      newDate.setHours(23,59,59,59);

      const timeToUserTimezoneOffset = newDate.getTimezoneOffset() * 60000;

      newDate = new Date(newDate.getTime() - timeToUserTimezoneOffset);
      formattedDate = newDate;
    }

    const params = { place_enquiry: { ...data, ...values, place_id: id, phone } };

    // params.tag = 'booking-form';
    // params.ab_code = 'booking-form-a';
    formattedDate && (params.place_enquiry.event_date = formattedDate);
    setLoading(true);

    !values.fifth_element && axios
      .post(url, params, { headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content }})
      .then(response => {

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'ga4-booking-form-submit',
          eventCategory: 'booking-form-submit',
          eventAction: 'booking-form-submit',
        });

        setLoading(false);
        response.data.status === 'created' ? setBookingSuccess(true) : setRequestError(true);
      })
      .catch(error => {
        setLoading(false);
        console.log(error);
        setRequestError(true);
      });
  };


  const submitFirstStep = (values) => {
    if (values) {
      setValues(prevValues => ({ ...prevValues, ...values }));
      setCurrentStep(2);
    }
  };

  const submitSecondStep = (values, step) => {
    if (values) {
      setValues(prevValues => ({ ...prevValues, ...values }));
      if (validationTrigger) {
        setValidationTrigger(false);
        setCurrentStep(1);
      } else {
        submitForm(values);
      }
    };

    step && setValidationTrigger(true);
  };

  const handleCancel = () => {
    setValues({});
    setCurrentStep(1);
    setIsOpen(false);
  };


  return (
    <div className={`booking-form ${isOpen ? 'mobile-open' : ''} booking-form--step-${currentStep} ${bookingSuccess ? 'success' : ''}`}>
      <div className={`booking-form__wrapper ${bookingSuccess ? 'success' : ''}`} ref={formWrapperRef}>

        <NotificationPopup
          customClass={'error'}
          handleClose={() => setRequestError(false)}
          message={'Noget gik galt. Prøv venligst senere eller kontakt os hvis fejlen opstår igen.'}
          visible={requestError} />

        <div className={`booking-form__top-border ${bookingSuccess ? 'success' : ''}`} />

        { !bookingSuccess &&
          <div className={`booking-form__step-navigators active-step-${currentStep}`}>
            <div className="booking-form__step-navigator" />
            <div className="booking-form__step-navigator" />
          </div>
        }

        { isOpen &&
          <div className="booking-form__mobile-close-btn"
               onClick={() => {
                 setIsOpen(false);
                 setBookingSuccess(false);
               }}>
            <SvgIcon name="close" size={24} />
          </div>
        }

        { bookingSuccess &&
          <div className="booking-form__success-wrapper">
            <h2 className="booking-form__headline">Tak for din henvendelse!</h2>
            <div className="booking-form__description success">
              <a href="/">{ siteName }</a>&nbsp;
              har modtaget din henvendelse og sendt den videre til
              &nbsp;<a href={`mailto: ${ email }`}>{ email }</a>.
            </div>
          </div>
        }

        { !bookingSuccess &&
          <div className="booking-form__content-wrapper">

            <h2 className="booking-form__headline">Bookingforespørgsel</h2>
            <p className="booking-form__description">Du kan sende en forespørgsel ved at bruge kontaktformularen herunder.</p>

            { currentStep === 1 &&
              <EventDetailsStep
                closeMobileForm={handleCancel}
                currentValues={values}
                loading={loading}
                placeName={placeName}
                submitButtonText={'Næste'}
                submitStep={submitFirstStep}
              />
            }

            { currentStep === 2 &&
              <ContactsStep
                closeMobileForm={handleCancel}
                currentValues={values}
                loading={loading}
                setValidationTrigger={setValidationTrigger}
                submitButtonText={'Send forespørgsel'}
                submitStep={submitSecondStep}
                validationTrigger={validationTrigger}
                userCountryPhoneCode={userCountryPhoneCode}
                goBack={
                  () => {
                    setCurrentStep(1);
                    setValidationTrigger(false);
                  }
                }
              />
            }

          </div>
        }

        { !bookingSuccess &&
          <Fragment>
            <div className="booking-form__mobile-trigger" onClick={() => setIsOpen(true)}>
              <div className="booking-form__submit-button-label">
                Send forespørgsel
              </div>
            </div>

            <div className="booking-form__footnote">
              Ingen binding eller betaling
            </div>

            { currentStep === 2 &&
              <div className="booking-form__back-button-wrapper">
                <div className="booking-form__back-button" onClick={() => submitSecondStep(null, 1)}>
                  <SvgIcon name="arrow_back" size={24} />
                  Tilbage
                </div>
              </div>
            }
          </Fragment>
        }

      </div>
    </div>
  )
};


export default BookingForm;
